import { useMemo, useState } from "react";
import ConfirmModal from "../../components/modals/ConfirmModal";
import InfoTooltip from "../../components/common/InfoTooltip";
import { SALON_SPEAK_TOOLTIPS } from "../../constants/salonSpeakTooltips";
import { speakText, speakVietnameseText } from "./speechUtils";

const categories = [
  "All",
  "Favorites",
  "Greetings",
  "Confirming Service",
  "Comfort Check",
  "Upsell",
  "Aftercare",
  "Payment & Tips",
];

const favoriteKey = (type, id) => `${type}:${id}`;

export default function SalonSpeakQuickPhrases({
  phrases,
  favorites,
  onToggleFavorite,
}) {
  const [category, setCategory] = useState("All");
  const [offlineModalOpen, setOfflineModalOpen] = useState(false);

  const rows = useMemo(() => {
    if (category === "All") return phrases || [];
    if (category === "Favorites") {
      return (phrases || []).filter((item) => favorites.has(favoriteKey("phrase", item.id)));
    }
    return (phrases || []).filter((item) => item.category === category);
  }, [phrases, category, favorites]);

  return (
    <div className="space-y-4 pb-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
            Quick Phrases
            <InfoTooltip text={SALON_SPEAK_TOOLTIPS.phrases.favorite} label="Favorites" />
          </div>
          <button
            type="button"
            onClick={() => setOfflineModalOpen(true)}
            className="h-9 rounded-full border border-slate-300 px-3 text-xs font-semibold text-slate-700"
          >
            Download offline pack
          </button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
                category === item
                  ? "bg-slate-900 text-white"
                  : "border border-slate-300 text-slate-700"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
          Phrase Board ({rows.length})
        </div>
        <div className="divide-y divide-slate-200">
          {rows.length ? (
            rows.map((row) => {
              const isFavorite = favorites.has(favoriteKey("phrase", row.id));
              return (
                <div
                  key={row.id}
                  className="px-4 py-3 flex items-start justify-between gap-3"
                >
                  <button
                    type="button"
                    onClick={() => {
                      void speakText(row.en, 1);
                    }}
                    className="text-left flex-1"
                  >
                    <div className="text-sm font-semibold text-slate-900">{row.en}</div>
                    <div className="text-xs text-slate-500 mt-1">{row.vi}</div>
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        void speakText(row.en, 0.8);
                      }}
                      className="rounded-full border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700"
                    >
                      Slow
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void speakText(row.en, 1);
                      }}
                      className="rounded-full border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700"
                    >
                      Normal
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (row.vi) {
                          void speakVietnameseText(row.vi, 0.8);
                        }
                      }}
                      className="rounded-full border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700"
                    >
                      VN Slow
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (row.vi) {
                          void speakVietnameseText(row.vi, 1);
                        }
                      }}
                      className="rounded-full border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700"
                    >
                      VN Normal
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleFavorite?.("phrase", row.id)}
                      className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${
                        isFavorite
                          ? "border-amber-300 bg-amber-50 text-amber-700"
                          : "border-slate-300 text-slate-600"
                      }`}
                    >
                      {isFavorite ? "Saved" : "Favorite"}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-4 text-sm text-slate-500">No phrases in this category.</div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={offlineModalOpen}
        title="Offline Pack"
        message="Available soon."
        subMessage={SALON_SPEAK_TOOLTIPS.phrases.offline}
        confirmLabel="Close"
        hideCancel
        onConfirm={() => setOfflineModalOpen(false)}
        onClose={() => setOfflineModalOpen(false)}
      />
    </div>
  );
}
